import { useMemoize } from '@vueuse/core';
import type { UserLimits } from '@/types/UserLimits';

import type {
  UserValidate,
  UserInfo,
  User,
  ForceRequestKycReasons,
} from '@/types/UserData';

const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;

async function logoutIfUserProfileError(error: any) {
  const { logout } = useAuthStore();
  const { openToaster } = useLayoutStore();
  const { t } = useTexts();

  await logout({
    displayMessage: false,
    redirectToHome: false,
  });

  const message =
    error.message ||
    error.data.message ||
    error.err ||
    `${t('general.errors.session_expired')} #UP001`;

  openToaster({
    message,
    type: 'error',
    time: 15000,
  });
}

const getUserProfile = useMemoize(
  async (isSpa: boolean = false): Promise<any> => {
    try {
      const { ApiService } = useApiService();

      const params = isSpa ? 'check_spa_again=1' : '';

      const { data } = await ApiService.get(
        '/auth/user-profile' + (params ? `?${params}` : ''),
      );

      return data;
    } catch (error) {
      await logoutIfUserProfileError(error);

      console.error(error);
    }
  },
);

const lastFetchDateInMs = ref<number>(+new Date());

export const useUserData = () => {
  const { user, zendeskUserToken } = storeToRefs(useUserDataStore());

  const { t } = useTexts();
  const genderOptions = [
    { value: '1', label: t('verification.docs.genderMale') },
    { value: '2', label: t('verification.docs.genderFemale') },
    { value: '0', label: t('verification.docs.genderNotSpecified') },
  ];

  async function fetchUserProfile(
    options: { force?: boolean; isSpa?: boolean } = {},
  ) {
    const { isLoading } = storeToRefs(useUserDataStore());
    const { setUserData, setLoading } = useUserDataStore();
    const { isAuthenticated } = storeToRefs(useAuthStore());
    const { setUserLimits } = useUserLimitsStore();

    isLoading.value = false;

    const { force = false, isSpa = false } = options;

    if (isLoading.value !== false && !force) {
      return false;
    }

    // Essa magia eu trouxe de outro arquivo
    await sleep(1);

    if (!isAuthenticated.value) {
      // Precisa deixar isso
      if (!isAuthenticated.value) {
        return;
      }
    }
    // Fim

    setLoading(true);

    const nowInMs = +new Date();

    if (nowInMs - lastFetchDateInMs.value > FIVE_MINUTES_IN_MS) {
      getUserProfile.clear();
    }

    lastFetchDateInMs.value = nowInMs;

    try {
      const data = force
        ? await getUserProfile.load(isSpa)
        : await getUserProfile(isSpa);

      setLoading(false);

      if (data) {
        setUserData(data);
        if (data.userInfo) {
          setUserLimits(data.userInfo as UserLimits);
        }
      }

      return data;
    } catch (error) {
      setLoading(false);
    }
  }

  const userId = computed(() => user.value?.id || 0);

  const userEmail = computed(() => user.value?.email || '');

  const userUsername = computed(() => user.value?.username || '');

  const userName = computed(() => user.value?.name || '');

  const userCountry = computed(() => {
    return user.value?.country || useRuntimeConfig().public.brand.country;
  });

  const userCurrency = computed(() => {
    return user.value?.currency || useRuntimeConfig().public.brand.currency;
  });

  const userLanguage = computed(() => {
    return user.value?.language || useRuntimeConfig().public.brand.language;
  });

  const userTimezone = computed(
    () => user.value?.timezone || useRuntimeConfig().public.brand.timezone,
  );

  const userBirthdate = computed(() => user.value?.birth_date || '');

  const userHasBirthdate = computed<boolean>(() => {
    return !!userBirthdate.value && userBirthdate.value !== '0000-00-00';
  });

  const userGender = computed(() => {
    const gender = user.value?.gender;

    return gender !== null && gender !== undefined ? String(gender) : undefined;
  });

  const userPhone = computed(() => user.value?.phone || '');

  const userPhoneDdi = computed(() => user.value?.ddi || '');

  const userDocument = computed(() => user.value?.document?.number || '');

  const userHasDocument = computed(() => {
    if (user.value && user.value?.document && user.value?.document?.number) {
      return true;
    } else {
      return false;
    }
  });

  const userAddressZipcode = computed(() => user.value?.zipcode);

  const userAddressCity = computed(() => user.value?.city);

  const userAddressState = computed(() => user.value?.state);

  const userAddressFullAddress = computed(() => user.value?.address);

  const userLastLogin = computed(() => user.value?.last_login);

  const isRegulatoryValidationEnabled = computed(
    () => user.value?.showPendingDataFlow,
  );

  const userValidationStatus = computed<string>(() => {
    const [lastValidation] = user.value?.user_validates?.slice(-1) || [];

    return lastValidation?.status || '';
  });

  const userValidationIsApproved = computed<boolean>(() => {
    return (
      userValidationStatus.value.toLocaleLowerCase().includes('aprovado') ||
      userValidationStatus.value.toLocaleLowerCase().includes('approved') ||
      userValidationStatus.value.toLocaleLowerCase().includes('aprova')
    );
  });

  const userValidationIsRepproved = computed<boolean>(() => {
    return (
      userValidationStatus.value.toLocaleLowerCase().includes('reprovado') ||
      userValidationStatus.value.toLocaleLowerCase().includes('repproved') ||
      userValidationStatus.value.toLocaleLowerCase().includes('reprova')
    );
  });

  const userValidationHistoryCount = computed<number>(() => {
    const allValidations = user.value?.user_validates || [];

    return allValidations.length;
  });

  const userValidationFullHistory = computed<UserValidate[]>(() => {
    const allValidations = user.value?.user_validates?.slice()?.reverse() || [];

    return allValidations;
  });

  const userNationalities = computed(() => {
    return normalizeStringJsonArray(user.value?.userInfo.nationalities);
  });

  const userRegisteredAt = computed(() => {
    return user.value?.created_at;
  });

  const isNewUserToValidate = computed<boolean>(() => {
    const isBetBrBrand = isBetBr();

    if (isBetBrBrand) {
      return true;
    }

    if (!userRegisteredAt.value) {
      return true;
    }

    // TODO: change this date in the exclusive repo when necessary
    const comparisonDate = new Date('2025-01-01');

    const registeredDate = new Date(userRegisteredAt.value);

    return registeredDate >= comparisonDate;
  });

  const userHasStrongPassword = computed<boolean>(() => {
    return !!user.value?.userInfo?.strong_password || false;
  });

  const isKycForcedRequest = computed<boolean>(() => {
    return !!user.value?.userInfo?.force_request_kyc || false;
  });

  const forceRequestKycReasons = computed<ForceRequestKycReasons[]>(() => {
    if (!isKycForcedRequest.value) {
      return [];
    }

    return user.value?.userInfo?.force_request_kyc_reasons || [];
  });

  const reasonsMap = {
    password_changed: 'verification.kyc.password_changed',
    reset_password: 'verification.kyc.reset_password',
    admin_change_password: 'verification.kyc.admin_change_password',
    location_changed_login: 'verification.kyc.location_changed_login',
    device_changed_login: 'verification.kyc.device_changed_login',
    ip_changed: 'verification.kyc.ip_changed',
    login_7_days_ago: 'verification.kyc.login_7_days_ago',
  };

  const forceRequestKycReason = computed<ForceRequestKycReasons>(() => {
    if (!isKycForcedRequest.value) {
      return '' as ForceRequestKycReasons;
    }

    const reasons = forceRequestKycReasons.value;

    for (const reason of reasons) {
      if (reason in reasonsMap) {
        return reason as ForceRequestKycReasons;
      }
    }

    return '' as ForceRequestKycReasons;
  });

  const forceKycRequestMessage = computed<string>(() => {
    if (!isKycForcedRequest.value) {
      return '';
    }

    const { t } = useTexts();
    const reason = forceRequestKycReason.value;

    if (reason && reason in reasonsMap) {
      return t(reasonsMap[reason]);
    }

    return t('verification.kyc.generic');
  });

  const userAcceptsMarketing = computed<boolean>(() => {
    return !!user.value?.userInfo?.mkt_accepted_at || false;
  });

  const userAffiliationCode = computed<string>(() => {
    return user.value?.affiliation_code || '';
  });

  const userAppSource = computed<string>(() => {
    return user.value?.app_source || '';
  });

  const isTwoFactorEnabled = computed<UserInfo['two_factor_enabled']>(() => {
    return !!user.value?.userInfo.two_factor_enabled;
  });

  const userTwoFactorType = computed<UserInfo['two_factor_type']>(() => {
    return user.value?.userInfo.two_factor_type || 'email';
  });

  const userBankAccounts = computed<User['bank_account']>(() => {
    return user.value?.bank_account || [];
  });

  const hasBankAccount = computed<boolean>(() => {
    return !!user.value?.has_bank_account;
  });

  const hasFirstDeposit = computed<boolean>(() => {
    return !!user.value?.ftd_value;
  });

  function setZendeskUserToken(token: string) {
    zendeskUserToken.value = token;
  }

  async function validateAddressByUser() {
    if (
      !userAddressZipcode.value ||
      !userAddressFullAddress.value ||
      !userAddressCity.value ||
      !userAddressState.value
    ) {
      const { ApiService } = useApiService();
      const { setUserAddress } = useUserDataStore();

      const { data } = await ApiService.get('/bff/users/address-by-user');

      if (data) {
        setUserAddress(data);
      }

      return data;
    }
  }

  return {
    fetchUserProfile,
    genderOptions,
    userId,
    userEmail,
    userUsername,
    userName,
    userCountry,
    userCurrency,
    userLanguage,
    userTimezone,
    userBirthdate,
    userHasBirthdate,
    userGender,
    userPhone,
    userPhoneDdi,
    userDocument,
    userHasDocument,
    userAddressZipcode,
    userAddressCity,
    userAddressState,
    userAddressFullAddress,
    userLastLogin,
    userValidationStatus,
    userValidationIsApproved,
    userValidationIsRepproved,
    userValidationFullHistory,
    userValidationHistoryCount,
    userNationalities,
    userRegisteredAt,
    isNewUserToValidate,
    userHasStrongPassword,
    isKycForcedRequest,
    forceRequestKycReason,
    forceKycRequestMessage,
    userAcceptsMarketing,
    userAffiliationCode,
    userAppSource,
    isTwoFactorEnabled,
    userTwoFactorType,
    userBankAccounts,
    hasBankAccount,
    hasFirstDeposit,
    isRegulatoryValidationEnabled,
    validateAddressByUser,
    zendeskUserToken,
    setZendeskUserToken,
  };
};
